import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, Sparkles, ArrowRight, Package } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

const STATUS_COLORS = {
  new: "bg-[#00D4FF]/15 text-[#00D4FF] border-[#00D4FF]/30",
  reviewing: "bg-violet-400/15 text-violet-300 border-violet-400/30",
  in_progress: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30",
  completed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  rejected: "bg-red-400/15 text-red-300 border-red-400/30",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // api is a stable module-level axios instance; setState functions are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quotes/me");
        setQuotes(data);
      } catch (e) {
        setError("Failed to load your quotes. Please refresh the page.");
      }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ dashboard</p>
              <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
                Hello, <span className="text-gradient-cyan">{user?.name?.split(" ")[0] || "Creator"}</span>.
              </h1>
              <p className="text-[#5A5A72] mt-3 max-w-lg">Your projects and quote history live here.</p>
            </div>
            <Link to="/quote" data-testid="dashboard-new-quote-btn" className="btn-primary px-5 py-3 rounded-lg text-sm font-semibold flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Quote
            </Link>
          </div>

          {/* Direct contact ribbon */}
          <div className="mt-8 glass-card rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#4B5563]">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00D4FF]">Need to chat?</span>{" "}
              Call or message Vedant directly for pricing & scope discussions.
            </p>
            <div className="flex gap-2">
              <a href="tel:+919902155712" data-testid="dashboard-phone-link" className="btn-ghost px-4 py-2 rounded-lg text-xs font-mono">+91 99021 55712</a>
              <a href="mailto:baligarvedant@gmail.com" data-testid="dashboard-email-link" className="btn-ghost px-4 py-2 rounded-lg text-xs font-mono">baligarvedant@gmail.com</a>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-4 mb-10">
            {[
              { label: "Total Quotes", value: quotes.length, accent: "cyan" },
              { label: "In Progress", value: quotes.filter((q) => q.status === "in_progress").length, accent: "yellow" },
              { label: "Completed", value: quotes.filter((q) => q.status === "completed").length, accent: "emerald" },
            ].map((s) => (
              <motion.div
                key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6"
              >
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">{s.label}</p>
                <p className="font-display font-black text-4xl mt-2 text-gradient-cyan">{s.value}</p>
              </motion.div>
            ))}
          </div>

          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="px-6 py-5 border-b border-black/[0.06] flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">Recent Quote Requests</h2>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#5A5A72]">{quotes.length} total</span>
            </div>
            {error && (
              <div className="p-6 border-b border-black/[0.06]" data-testid="dashboard-error">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            {loading ? (
              <Loader label="LOADING YOUR QUOTES..." />
            ) : quotes.length === 0 ? (
              <div className="p-12 text-center" data-testid="dashboard-empty">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-4 bg-gradient-to-tr from-cyan-400/20 to-violet-400/20 grid place-items-center border border-[#00D4FF]/20">
                  <Sparkles className="w-6 h-6 text-[#00D4FF]" />
                </div>
                <p className="font-display text-lg">No quotes yet.</p>
                <p className="text-[#6B7280] text-sm mt-1">Submit your first project request to see it here.</p>
                <Link to="/quote" className="mt-6 inline-flex btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold">Start a project</Link>
              </div>
            ) : (
              <div>
                {quotes.map((q, i) => (
                  <motion.div
                    key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    data-testid={`quote-row-${q.id}`}
                    className="px-6 py-5 border-b border-black/[0.06] last:border-b-0 hover:bg-white/[0.015] transition"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/10 grid place-items-center border border-[#00D4FF]/20">
                          <FileText className="w-4 h-4 text-[#00D4FF]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold">{q.project_title}</p>
                          <p className="text-xs text-[#00D4FF] font-mono uppercase tracking-wider mt-1">{q.service_type}</p>
                          <p className="text-sm text-[#5A5A72] mt-2 line-clamp-2 max-w-xl">{q.description}</p>
                          <div className="flex flex-wrap gap-3 mt-3 text-xs text-[#6B7280] font-mono uppercase tracking-wider">
                            {q.budget_range && <span>Budget: {q.budget_range}</span>}
                            {q.timeline && <span>Timeline: {q.timeline}</span>}
                            <span>{new Date(q.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="mt-3 flex items-center gap-3">
                            <div className="flex-1 max-w-xs h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-cyan-400 to-teal-400" style={{ width: `${q.progress || 0}%` }} />
                            </div>
                            <span className="text-xs font-mono text-[#00D4FF]">{q.progress || 0}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <span className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}>
                          {q.status.replace("_", " ")}
                        </span>
                        <Link
                          to={`/track/${q.id}`}
                          data-testid={`track-link-${q.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2060EE]/10 text-[#2060EE] text-xs font-semibold hover:bg-[#2060EE]/20 transition"
                        >
                          <Package className="w-3.5 h-3.5" /> Track Order
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
